import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const programs = [
  {
    id: 1,
    name: 'Mixed Martial Arts',
    shortName: 'MMA',
    description: 'Complete combat training combining striking, grappling, and ground fighting techniques for ultimate fighting skills.',
    image: 'https://images.unsplash.com/photo-1737381508529-a110d717d5a7?q=85&w=800&auto=format&fit=crop',
    featured: true,
  },
  {
    id: 2,
    name: 'Brazilian Jiu-Jitsu',
    shortName: 'BJJ',
    description: 'Master the gentle art of ground fighting, submissions, and positional control.',
    image: 'https://images.unsplash.com/photo-1644594570589-ef85bd03169f?q=85&w=800&auto=format&fit=crop',
    featured: false,
  },
  {
    id: 3,
    name: 'Muay Thai',
    shortName: 'MT',
    description: 'The art of eight limbs. Learn devastating kicks, knees, elbows, and clinch work.',
    image: 'https://images.unsplash.com/photo-1773289336870-862f5eb371fc?q=85&w=800&auto=format&fit=crop',
    featured: false,
  },
  {
    id: 4,
    name: 'Muay Boran',
    shortName: 'MB',
    description: 'Ancient Thai martial art, the predecessor of Muay Thai with traditional combat techniques.',
    image: 'https://images.unsplash.com/photo-1696454411278-a64de1369e83?q=85&w=800&auto=format&fit=crop',
    featured: false,
  },
  {
    id: 5,
    name: 'Boxing',
    shortName: 'BOX',
    description: 'Develop lightning-fast hands, footwork, and defensive skills in the sweet science.',
    image: 'https://images.unsplash.com/photo-1561532325-7d5231a2dede?q=85&w=800&auto=format&fit=crop',
    featured: false,
  },
  {
    id: 6,
    name: 'Kickboxing',
    shortName: 'KB',
    description: 'Powerful striking combining punches and kicks for fitness and self-defense.',
    image: 'https://images.unsplash.com/photo-1773289338631-17455d5c3b73?q=85&w=800&auto=format&fit=crop',
    featured: false,
  },
  {
    id: 7,
    name: 'Self Defense',
    shortName: 'SD',
    description: 'Practical techniques for real-world protection and personal safety scenarios.',
    image: 'https://images.unsplash.com/photo-1773289338026-fc1f48a88644?q=85&w=800&auto=format&fit=crop',
    featured: false,
  },
  {
    id: 8,
    name: 'Kids Martial Arts',
    shortName: 'KMA',
    description: 'Building confidence, discipline, and fitness in young warriors aged 5-15.',
    image: 'https://images.unsplash.com/photo-1769095216189-0ae27b6cc726?q=85&w=800&auto=format&fit=crop',
    featured: false,
  },
];

const ProgramCard = ({ program, index, isInView }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`program-card group relative overflow-hidden border border-[#262626] cursor-pointer ${
        program.featured ? 'md:col-span-2 md:row-span-2' : ''
      }`}
      data-testid={`program-card-${program.id}`}
    >
      {/* Image */}
      <div className="absolute inset-0">
        <img
          src={program.image}
          alt={program.name}
          className="program-image w-full h-full object-cover"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
      </div>

      {/* Content */}
      <div className={`relative h-full flex flex-col justify-end p-6 ${program.featured ? 'md:p-8' : ''}`}>
        {/* Short name badge */}
        <span className="absolute top-4 right-4 bg-[#DC2626] text-white text-xs font-bold px-3 py-1 tracking-wider">
          {program.shortName}
        </span>

        <h3 className={`font-['Oswald'] text-white font-bold uppercase tracking-wide mb-2 ${
          program.featured ? 'text-3xl md:text-4xl' : 'text-xl md:text-2xl'
        }`}>
          {program.name}
        </h3>
        
        <p className={`text-neutral-400 mb-4 line-clamp-2 ${
          program.featured ? 'text-base md:line-clamp-3' : 'text-sm'
        }`}>
          {program.description}
        </p>

        {/* Learn more link */}
        <div className="flex items-center gap-2 text-[#DC2626] text-sm font-bold uppercase tracking-wider group-hover:gap-4 transition-all">
          <span>Learn More</span>
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>

      {/* Hover border effect */}
      <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#DC2626] transition-colors pointer-events-none" />
    </motion.div>
  );
};

const ProgramsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="programs"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#0A0A0A]"
      data-testid="programs-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-[#DC2626] text-sm font-bold tracking-[0.3em] uppercase">
            Our Programs
          </span>
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Choose Your<br />
            <span className="text-[#DC2626]">Fighting Style</span>
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto">
            From striking to grappling, we offer comprehensive training in all major martial arts disciplines.
            Find the perfect program for your goals.
          </p>
        </motion.div>

        {/* Programs Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[250px] md:auto-rows-[280px]">
          {programs.map((program, index) => (
            <ProgramCard
              key={program.id}
              program={program}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProgramsSection;
